import * as React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { LANGUAGE_LEVELS, type LanguageEntry } from '@clawhire/shared';
import type { Option } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SimpleSelect } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export interface LanguagesEditorProps {
  value: LanguageEntry[];
  onChange: (value: LanguageEntry[]) => void;
  readOnly?: boolean;
  /** Known languages (`GET /options` → `language`), offered as a datalist. */
  suggestions?: readonly string[];
  className?: string;
}

function levelLabel(level: string): string {
  switch (level) {
    case 'native':
      return 'Native';
    case 'beginner':
      return 'Beginner';
    case 'not stated':
      return 'Not stated';
    default:
      return level;
  }
}

const LEVEL_OPTIONS: Option[] = LANGUAGE_LEVELS.map((l) => ({ value: l, label: levelLabel(l) }));

function optionsFor(level: string): Option[] {
  return LEVEL_OPTIONS.some((o) => o.value === level) || !level
    ? LEVEL_OPTIONS
    : [{ value: level, label: level }, ...LEVEL_OPTIONS];
}

/**
 * Table of language + level (CEFR, native, beginner, not stated) with add / edit / remove.
 * Controlled: the parent keeps the array and saves it (`PATCH { languages }`).
 */
export function LanguagesEditor({
  value,
  onChange,
  readOnly = false,
  suggestions = [],
  className,
}: LanguagesEditorProps) {
  const listId = React.useId();
  const update = (index: number, patch: Partial<LanguageEntry>) =>
    onChange(value.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));
  const add = () => onChange([...value, { language: '', level: 'not stated' }]);

  return (
    <div className={cn('space-y-3', className)}>
      {value.length === 0 ? (
        <p className="text-sm text-muted-foreground">No languages recorded.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Language</TableHead>
                <TableHead className="w-48">Level</TableHead>
                {readOnly ? null : (
                  <TableHead className="w-12">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {value.map((entry, i) => (
                <TableRow key={i} className="hover:bg-transparent">
                  <TableCell>
                    {readOnly ? (
                      <span className="font-medium">{entry.language || '–'}</span>
                    ) : (
                      <Input
                        list={suggestions.length ? listId : undefined}
                        value={entry.language}
                        onChange={(e) => update(i, { language: e.target.value })}
                        placeholder="e.g. German"
                        aria-label={`Language ${i + 1}`}
                        className="h-8"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {readOnly ? (
                      levelLabel(entry.level)
                    ) : (
                      <SimpleSelect
                        value={entry.level || 'not stated'}
                        onChange={(v) => update(i, { level: v ?? 'not stated' })}
                        options={optionsFor(entry.level)}
                        aria-label={`Level of language ${i + 1}`}
                        className="h-8"
                      />
                    )}
                  </TableCell>
                  {readOnly ? null : (
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove language ${entry.language || i + 1}`}
                        onClick={() => remove(i)}
                      >
                        <Trash2 aria-hidden="true" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {readOnly ? null : (
        <>
          <Button type="button" variant="outline" size="sm" onClick={add}>
            <Plus aria-hidden="true" /> Add language
          </Button>
          {suggestions.length > 0 ? (
            <datalist id={listId}>
              {suggestions.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          ) : null}
        </>
      )}
    </div>
  );
}
